// Orchestration layer for M5 employee management.
//
// Responsibilities:
//   - Generate a strong random password for new employees (never logged).
//   - Call the existing `registerEmployee` Cloud Function (same one the
//     legacy onboarding modal used).
//   - Persist the iOS-parity employee paths via `firebase-business.js`.
//   - Map low-level errors to UI-friendly messages.
//   - Surface the freshly-minted credentials to the dashboard so the UI
//     can show a one-time copy modal (no programmatic transactional
//     email-sender exists in the repo today - `emailSender.js` is wired
//     to the marketing contact form template, not credential delivery).
//
// Defence-in-depth: callers are expected to gate by role *before* invoking
// these functions, but the helpers themselves don't trust the UI - they
// validate inputs and surface a stable error shape so the page can render
// the right message regardless of how the call site was reached.

import {
  saveEmployeeRecords,
  deleteEmployeeRecords,
  replaceEmployeeRecord,
  sanitiseEmailKey,
} from "./firebase-business.js";
import { sendEmployeeCredentials } from "./emailSender.js";
import { track } from "./analytics.js";
import {
  CLOUD_FUNCTIONS_BASE_URL,
  CLOUD_FUNCTIONS_AUTH_TOKEN,
} from "./firebase-functions.js";

const VALID_ROLES = new Set(["Owner", "Administrator", "Employee"]);

// Friendly defaults - keep raw Firebase / Cloud Function codes out of the UI.
const ERROR_MESSAGES = {
  "auth/email-already-exists":
    "An account with this email already exists. Ask the user to sign in instead, or use a different email address.",
  "auth/email-already-in-use":
    "An account with this email already exists. Ask the user to sign in instead, or use a different email address.",
  "auth/invalid-email":
    "That email address doesn't look valid. Please double-check it.",
  "auth/weak-password":
    "We couldn't create a strong enough password. Please try again.",
};

const callBackend = async (endpoint, body) => {
  const response = await fetch(`${CLOUD_FUNCTIONS_BASE_URL}/${endpoint}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: CLOUD_FUNCTIONS_AUTH_TOKEN,
    },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    let parsed = {};
    try {
      parsed = await response.json();
    } catch (e) {
      // Non-JSON error body - fall through to a generic message below.
    }
    const code = parsed.code || parsed.error || `HTTP ${response.status}`;
    const friendly = ERROR_MESSAGES[code];
    const error = new Error(
      friendly ||
        "We couldn't complete that request. Please try again in a moment."
    );
    error.code = code;
    throw error;
  }
  return response.json().catch(() => ({}));
};

// Crypto-grade random password (16 chars). Mix of upper/lower/digits/symbols
// so the Cloud Function's create-user step always satisfies the Firebase
// Auth strength rules. NEVER pass the returned value through `console.log`.
export const generateStrongPassword = (length = 16) => {
  const upper = "ABCDEFGHJKLMNPQRSTUVWXYZ";
  const lower = "abcdefghijkmnopqrstuvwxyz";
  const digits = "23456789";
  const symbols = "!@#$%^&*-_+=?";
  const pools = [upper, lower, digits, symbols];
  const all = upper + lower + digits + symbols;
  const len = Math.max(12, length);

  const buffer = new Uint32Array(len);
  if (typeof window !== "undefined" && window.crypto && window.crypto.getRandomValues) {
    window.crypto.getRandomValues(buffer);
  } else {
    // Fallback for non-browser test environments. Acceptable because the
    // password is only ever sent over TLS to the Cloud Function - but we
    // still want broad entropy.
    for (let i = 0; i < len; i += 1) {
      buffer[i] = Math.floor(Math.random() * 0xffffffff);
    }
  }

  const chars = [];
  // Guarantee at least one char from each pool by seeding the first four.
  for (let i = 0; i < pools.length; i += 1) {
    const pool = pools[i];
    chars.push(pool[buffer[i] % pool.length]);
  }
  for (let i = pools.length; i < len; i += 1) {
    chars.push(all[buffer[i] % all.length]);
  }

  // Fisher-Yates shuffle so the guaranteed-class chars aren't always
  // at positions 0..3.
  for (let i = chars.length - 1; i > 0; i -= 1) {
    const j = buffer[i] % (i + 1);
    const tmp = chars[i];
    chars[i] = chars[j];
    chars[j] = tmp;
  }
  return chars.join("");
};

const normaliseEmployee = (employee) => {
  if (!employee || typeof employee !== "object") {
    throw new Error("Employee details are required.");
  }
  const name = String(employee.name || "").trim();
  const email = String(employee.email || "").trim().toLowerCase();
  const role = String(employee.role || "Employee").trim();
  if (!name) throw new Error("Employee name is required.");
  if (!email) throw new Error("Employee email is required.");
  if (!VALID_ROLES.has(role)) throw new Error("Pick a role for this employee.");
  return { name, email, role };
};

// Returns `{ employee, credentials, emailSent }`. The page can display the
// credentials one-time copy modal; the password is never persisted client-side.
// `opts.businessDisplayName` is used in the registration email template; pass
// the human-readable name if known so the recipient sees their employer.
export const addEmployee = async (businessId, rawEmployee, opts = {}) => {
  if (!businessId) throw new Error("Select a business first.");
  const employee = normaliseEmployee(rawEmployee);
  if (employee.role === "Owner") {
    throw new Error("Only one owner is allowed per business.");
  }

  const password = generateStrongPassword(16);

  try {
    await callBackend("registerEmployee", {
      userData: { email: employee.email, password },
    });
  } catch (error) {
    track("dashboard_employee_add_failed", {
      business_id: businessId,
      step: "register_employee",
      // Intentionally omit raw error text so PII / codes don't leak.
    });
    throw error;
  }

  try {
    await saveEmployeeRecords(businessId, employee);
  } catch (error) {
    track("dashboard_employee_add_failed", {
      business_id: businessId,
      step: "save_records",
    });
    throw new Error(
      "We created the account but couldn't link it to your business. Please contact support."
    );
  }

  // Mirror iOS `ManageEmployeeViewModel.executeEmployeeRegistration` -
  // send the credentials email via EmailJS. Best-effort: a failure here
  // does NOT roll back the registration (the Auth user + RTDB record both
  // exist). The page falls back to the credentials modal so the admin can
  // share the password manually.
  let emailSent = false;
  try {
    await sendEmployeeCredentials({
      businessName: String(opts.businessDisplayName || businessId),
      recipientName: employee.name,
      recipientEmail: employee.email,
      password,
    });
    emailSent = true;
    track("dashboard_employee_registration_email_sent", {
      business_id: businessId,
    });
  } catch (error) {
    track("dashboard_employee_registration_email_failed", {
      business_id: businessId,
    });
  }

  track("dashboard_employee_added", {
    business_id: businessId,
    role: employee.role,
  });

  return {
    employee,
    credentials: { email: employee.email, password },
    sanitisedEmail: sanitiseEmailKey(employee.email),
    emailSent,
  };
};

// Updates an employee's name/role and, when the email changes, performs
// a single atomic multi-path RTDB update so we can't end up with orphan
// records or dual-sign-in if a partial write fails.
//
// `opts.currentUserEmail` is the signed-in user's email. When the row
// being edited matches that email (case-insensitive) AND the role is
// being changed, we throw - defence-in-depth against a UI bypass that
// would otherwise let an Administrator demote themselves and lock out.
export const updateEmployee = async (
  businessId,
  original,
  rawEmployee,
  opts = {}
) => {
  if (!businessId) throw new Error("Select a business first.");
  if (!original || !original.email) {
    throw new Error("Cannot update an employee without their original record.");
  }
  const next = normaliseEmployee(rawEmployee);
  const previousEmail = String(original.email || "").toLowerCase();

  if (next.role === "Owner" && original.role !== "Owner") {
    throw new Error("The owner role can't be reassigned.");
  }
  if (original.role === "Owner" && next.role !== "Owner") {
    throw new Error("The owner role can't be reassigned.");
  }

  const currentUserEmail = String(opts.currentUserEmail || "").toLowerCase();
  if (
    currentUserEmail &&
    previousEmail === currentUserEmail &&
    String(original.role || "") !== next.role
  ) {
    throw new Error("You cannot change your own role.");
  }

  try {
    if (previousEmail && previousEmail !== next.email) {
      // Email is changing - do the whole thing in one atomic multi-path
      // write. `replaceEmployeeRecord` handles the employeesData rebuild,
      // both per-business `employees/<email>` flags and both global
      // `employees/<email>` lists in a single update().
      await replaceEmployeeRecord(
        businessId,
        { email: previousEmail, role: original.role },
        next
      );
    } else {
      // No email change - the existing save path already builds a single
      // multi-path update for the three iOS-parity locations.
      await saveEmployeeRecords(businessId, next);
    }
  } catch (error) {
    track("dashboard_employee_update_failed", { business_id: businessId });
    // Preserve the underlying message when it's a deliberate guard error.
    if (error && error.message && error.message.startsWith("You cannot")) {
      throw error;
    }
    throw new Error(
      "We couldn't save those changes. Please try again in a moment."
    );
  }

  track("dashboard_employee_updated", {
    business_id: businessId,
    email_changed: previousEmail !== next.email,
  });
  return { employee: next };
};

export const deleteEmployee = async (businessId, employee) => {
  if (!businessId) throw new Error("Select a business first.");
  if (!employee || !employee.email) {
    throw new Error("Cannot remove an employee without their email.");
  }
  if (employee.role === "Owner") {
    throw new Error("The owner of a business can't be removed.");
  }
  const normalisedEmail = String(employee.email).toLowerCase();

  try {
    await callBackend("deleteEmployee", {
      email: normalisedEmail,
      businessId,
    });
  } catch (error) {
    track("dashboard_employee_delete_failed", {
      business_id: businessId,
      step: "delete_employee",
    });
    throw error;
  }

  try {
    await deleteEmployeeRecords(businessId, { email: normalisedEmail });
  } catch (error) {
    track("dashboard_employee_delete_failed", {
      business_id: businessId,
      step: "delete_records",
    });
    throw new Error(
      "We removed the account but couldn't clean up the business records. Please contact support."
    );
  }

  track("dashboard_employee_deleted", { business_id: businessId });
  return { ok: true };
};
